/**
 * The typefaces a tenant may choose for its public pages, and how to load and apply them
 * (TDD-SA FR-SA-140).
 *
 * The list mirrors the backend's `SettingsSettingKeys.DisplayFonts` and `BodyFonts`: the server only
 * accepts these names, and this file is the one place that knows how to fetch each from Google Fonts.
 * One catalogue for every app, so the booking page and the salon admin's preview of it can never load
 * a family differently. A family not listed here is ignored and the page keeps the token defaults.
 */

interface BrandFont {
  /** The `css2` axis spec, checked against fonts.googleapis.com when the family was added; empty for a single-style face. */
  axes: string;
  /** The generic family the stack falls back to while the face loads. */
  fallback: "serif" | "sans-serif";
}

const CATALOGUE: Record<string, BrandFont> = {
  // The defaults, from the owner's Organic Wellness style (2026-10-28): Instrument Serif over Figtree.
  // Instrument Serif has one weight and a true italic, which sets the headline's highlighted words.
  "Instrument Serif": { axes: "ital@0;1", fallback: "serif" },
  // Marcellus has one style only.
  Marcellus: { axes: "", fallback: "serif" },
  Mulish: { axes: "ital,wght@0,200..1000;1,200..1000", fallback: "sans-serif" },
  Figtree: { axes: "ital,wght@0,300..900;1,300..900", fallback: "sans-serif" },
  Sora: { axes: "wght@100..800", fallback: "sans-serif" },
  "Bodoni Moda": { axes: "ital,opsz,wght@0,6..96,400..700;1,6..96,400..700", fallback: "serif" },
  "Playfair Display": { axes: "ital,wght@0,400..700;1,400..700", fallback: "serif" },
  "Cormorant Garamond": { axes: "ital,wght@0,400..700;1,400..700", fallback: "serif" },
  "DM Serif Display": { axes: "ital@0;1", fallback: "serif" },
  Fraunces: { axes: "ital,opsz,wght@0,9..144,400..700;1,9..144,400..700", fallback: "serif" },
  "Plus Jakarta Sans": { axes: "ital,wght@0,400..800;1,400..800", fallback: "sans-serif" },
  Poppins: { axes: "ital,wght@0,400;0,500;0,600;0,700;1,400;1,500", fallback: "sans-serif" },
  Montserrat: { axes: "ital,wght@0,400..800;1,400..800", fallback: "sans-serif" },
  Inter: { axes: "ital,opsz,wght@0,14..32,400..700;1,14..32,400..700", fallback: "sans-serif" },
  "DM Sans": { axes: "ital,opsz,wght@0,9..40,400..800;1,9..40,400..800", fallback: "sans-serif" },
  Manrope: { axes: "wght@400..800", fallback: "sans-serif" },
  Lato: { axes: "ital,wght@0,400;0,700;1,400", fallback: "sans-serif" },
  "Nunito Sans": {
    axes: "ital,opsz,wght@0,6..12,400..800;1,6..12,400..800",
    fallback: "sans-serif",
  },
};

/** Every family this build can load. */
export const BRAND_FONT_FAMILIES: readonly string[] = Object.keys(CATALOGUE);

/** The CSS `font-family` value for a known family, or null for one this build cannot load. */
export function brandFontStack(family: string): string | null {
  const font = CATALOGUE[family];
  if (!font) return null;
  return font.fallback === "serif"
    ? `"${family}", Georgia, "Times New Roman", serif`
    : `"${family}", system-ui, -apple-system, "Segoe UI", sans-serif`;
}

/** Whether a known family is a serif or a sans, so an app can tune weight and spacing; null when unknown. */
export function brandFontKind(family: string): "serif" | "sans-serif" | null {
  return CATALOGUE[family]?.fallback ?? null;
}

/** One Google Fonts stylesheet address for the known families given, or null when none is known. */
export function brandFontHref(families: readonly string[]): string | null {
  const known = [...new Set(families)].filter((family) => CATALOGUE[family]);
  if (known.length === 0) return null;
  const params = known
    .map((family) => {
      const axes = CATALOGUE[family]?.axes;
      return `family=${family.replaceAll(" ", "+")}${axes ? `:${axes}` : ""}`;
    })
    .join("&");
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}

export interface BrandFonts {
  display: string;
  body: string;
}

const LINK_MARK = "data-brand-fonts";

/**
 * Loads the two families and writes them onto `--brand-font-display` and `--brand-font-body`, as
 * `applyBranding` writes the accent. They are not the `--t-font-*` tokens on purpose: the library uses
 * `--t-font-display` for buttons, labels and badges too, and an editorial serif there would set every
 * button in it. Each app maps the two onto its own type: the booking app puts the display face on its
 * headlines only. The stylesheet is attached, never awaited: text paints at once in the fallback face and
 * swaps when the font arrives (`display=swap`), so a slow font never delays the first paint
 * (NFR-SA-009). Returns a function that puts back the tokens and removes the stylesheet.
 */
export function applyBrandFonts(fonts: BrandFonts, root: HTMLElement): () => void {
  const entries: [string, string | null][] = [
    ["--brand-font-display", brandFontStack(fonts.display)],
    ["--brand-font-body", brandFontStack(fonts.body)],
  ];
  const previous = entries.map(([name]) => [name, root.style.getPropertyValue(name)] as const);
  for (const [name, value] of entries) {
    if (value) root.style.setProperty(name, value);
  }

  const href = brandFontHref([fonts.display, fonts.body]);
  const doc = root.ownerDocument;
  let link: HTMLLinkElement | null = null;
  const loaded = [...doc.head.querySelectorAll<HTMLLinkElement>(`link[${LINK_MARK}]`)].some(
    (existing) => existing.getAttribute("href") === href,
  );
  if (href && !loaded) {
    link = doc.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute(LINK_MARK, "");
    doc.head.append(link);
  }

  return () => {
    for (const [name, value] of previous) {
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    }
    link?.remove();
  };
}
